<template>
  <div v-if="hasChecksForDisplay" :class="containerClass">
    <div
      v-if="!isSfAssistantReworkSupported"
      class="
        sf-pad-v-2 sf-pad-h-3 sf-layer-border-bottom sf-level sf-level-between
      "
    >
      <div>
        <div class="sf-font-medium sf-assistant-sidepane-list__title">
          {{ titleText }}
        </div>
        <div class="sf-font-xs sf-c-text-light">
          {{ subtitleText }}
          <span v-tooltip.timestamp="timestamp">
            {{ humanizedTimestamp }}
          </span>
        </div>
      </div>
      <a class="sf-btn sf-btn-small" :href="overviewUrl" target="_blank">
        {{ overviewText }}
        <i class="icon icon-external-link-square"></i>
      </a>
    </div>

    <SfFlash v-if="error" type="alert" :message="tryAgainText" />

    <template v-for="check in visibleChecks">
      <template v-if="shouldRenderCheck(check)">
        <sf-assistant-check
          v-if="!onlyTodos || remarks[check.id]"
          :key="check.id"
          :check="check"
          :remark="remarks[check.id]"
          :dismissed="checkIsDismissedOrCompleted(check)"
          :completed="checkIsCompleted(check)"
          @toggle-dismissal="toggleCheckDismissal"
          @add-todo="addTodo"
          @trigger-action="triggerAction"
        />
      </template>
    </template>

    <div
      v-if="canToggleHiddenChecksVisibility"
      class="sf-pad-v-1 sf-align-c sf-layer-border-bottom"
      @click.stop.prevent="toggleHidden"
    >
      <a href="">
        <span v-if="showHidden || onlyTodos"
          >{{ hideText }} ({{ hiddenChecks.length }})</span
        >
        <span v-else>{{ showHiddenText }} ({{ hiddenChecks.length }})</span>
      </a>
    </div>

    <sf-assistant-check
      v-for="check in effectiveHiddenChecks"
      :key="check.id"
      :check="check"
      :remark="remarks[check.id]"
      :dismissed="check.dismissed"
      :completed="check.completed"
      @toggle-dismissal="toggleCheckDismissal"
      @add-todo="addTodo"
      @trigger-action="triggerAction"
    />
  </div>
</template>

<script setup lang="ts">
import SfAssistantCheck from './sf-assistant-check.vue'
import { sfAssistantOverviewUrl } from '../utils'
import SfFlash from '@app/shared/vue/components/sf-flash.vue'
import { ref, computed } from 'vue'
import { SfTime } from '@app/adapters/time'
import type { Check } from '../types'
import { storeToRefs } from 'pinia'
import { useChecksStore } from '@app/application/sf-assistant-checks/stores/checks'
import useSfAssistantChecks from '@app/application/sf-assistant-checks/composables/use-sf-assistant-checks'
import {
  checkIsCompleted,
  checkIsDismissedOrCompleted,
  doneChecks,
  pendingChecks,
} from '@app/application/sf-assistant-checks/utils'
import { triggerAction } from '@app/application/sf-assistant-checks/actions'
import { isFeatureSupported } from '@app/adapters/silverfin'

const checksStore = useChecksStore()
useSfAssistantChecks()
import { Remark } from '@app/application/communication/types'

const {
  checks,
  accountId,
  reconciliationId,
  timestamp,
  visibleChecks,
  hiddenChecks,
  error,
  hasChecks,
} = storeToRefs(checksStore)

const { toggleCheckDismissal } = checksStore

interface Props {
  remarks: Record<string, Remark>
  expanded?: boolean
  onlyTodos?: boolean
  done?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  expanded: false,
  onlyTodos: false,
  done: false,
})

const showHidden = ref(props.expanded)

// translations
const titleText = window.I18n.t('account_entry.assistant.sidepane.title')
const subtitleText = window.I18n.t('account_entry.assistant.sidepane.subtitle')
const overviewText = window.I18n.t('account_entry.assistant.sidepane.overview')
const tryAgainText = window.I18n.t('common.error.try_again')
const hideText = window.I18n.t('account_entry.assistant.sidepane.hide_hidden')
const showHiddenText = window.I18n.t(
  'account_entry.assistant.sidepane.show_hidden'
)
const addTodoTitle = window.I18n.t(
  'account_entry.assistant.check.add_todo_title'
)
const addTodoSaveButtonText = window.I18n.t(
  'account_entry.assistant.check.add_todo_save_button_label'
)

const isSfAssistantReworkSupported = isFeatureSupported(
  'supports_sf_assistant_rework'
)

const isCommunicationReworkSupported = isFeatureSupported(
  'supports_communications_rework'
)

const containerClass = {
  'tw-flex tw-flex-col tw-gap-4': isSfAssistantReworkSupported,
  'tw-mb-4': isSfAssistantReworkSupported && !isCommunicationReworkSupported,
  'tw-mx-6 tw-pt-4':
    isSfAssistantReworkSupported && isCommunicationReworkSupported,
  'sf-layer-rounded sf-layer-border sf-bg-white sf-below-2 sf-layer-shadow':
    !isSfAssistantReworkSupported,
}

const effectiveHiddenChecks = computed(() => {
  if (!(showHidden.value || props.onlyTodos)) {
    return []
  }

  return hiddenChecks.value.filter(shouldRenderCheck).map((check) => ({
    ...check,
    dismissed: checkIsDismissedOrCompleted(check),
    completed: checkIsCompleted(check),
  }))
})

const overviewUrl = computed(() => {
  const objectId = accountId.value ? accountId.value : reconciliationId.value
  const objectType = accountId.value ? 'Account' : 'Reconciliation'
  return sfAssistantOverviewUrl(objectType, objectId)
})

const humanizedTimestamp = computed(() => {
  return SfTime(timestamp.value).fromNow()
})

const hasChecksForDisplay = computed(() => {
  //return false if we have no checks
  if (!hasChecks.value) {
    return false
  }

  //if not in only todos mode return true since there are some checks
  if (!props.onlyTodos) {
    return true
  }

  //depending what kind of todos we are return if checks are present
  return props.done
    ? doneChecks(checks.value, props.remarks).length > 0
    : pendingChecks(checks.value, props.remarks).length > 0
})

const canToggleHiddenChecksVisibility = computed(
  () =>
    !isSfAssistantReworkSupported &&
    hiddenChecks.value.length > 0 &&
    !props.expanded
)

function toggleHidden(): void {
  showHidden.value = !showHidden.value
}

function shouldRenderCheck(check: Check): boolean {
  if (!props.onlyTodos) {
    return true
  }

  const remark = props.remarks[check.id]
  return remark ? remark.done === props.done : false
}

function addTodo(check: Check): void {
  const ownerType =
    check.accountId !== null && check.accountId !== 0
      ? 'Account'
      : 'ReconciliationText'
  const ownerId =
    ownerType === 'Account' ? check.accountId : check.reconciliationId

  window.modalCitizen.openModal('new-remark', {
    ownerType: ownerType,
    ownerId: ownerId,
    ledgerId: window.Silverfin.currentLedgerId(),
    allRemarkTags: window.allRemarkTags,
    showTags: true,
    type: 'todo',
    hideTypeSelector: true,
    hidePeriodSelector: true,
    title: addTodoTitle,
    saveButtonLabel: addTodoSaveButtonText,
    assistantCheckHash: check.hash,
    assistantCheckId: check.id,
  })
}
</script>

<style lang="scss" scoped>
.sf-assistant-sidepane-list__title {
  margin-bottom: 4px;
}
</style>
